import { Component, EventEmitter, inject, OnDestroy, OnInit, Output } from '@angular/core';
import { NgIf } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { StateNotifyService } from '../../../../common/state/service/state-notify.service';
import { VesselDpControl } from '../../dto/vessel-dp-control';
import { VesselDpControlDetailsComponent } from '../vessel-dp-control-details/vessel-dp-control-details.component';
import { Vessel } from '../../dto/vessel';
import { Subject, takeUntil } from 'rxjs';
import { VesselDpControlService } from '../../service/vessel-dp-control.service';

@Component({
  selector: 'dpi-vessel-dp-control-list',
  imports: [MatCardModule, MatTableModule, MatIconModule, MatButtonModule, MatToolbarModule, MatDialogModule, MatTooltipModule, NgIf],
  templateUrl: './vessel-dp-control-list.component.html',
  styleUrl: './vessel-dp-control-list.component.scss'
})
export class VesselDpControlListComponent implements OnInit, OnDestroy {
  vessel: Vessel;
  displayedColumns: string[] = ['name', 'description'];
  dataSource: VesselDpControl[] = [];
  destroyed$ = new Subject<void>(); // notifier of component destroyed
  @Output('listSizeChanged') listSize = new EventEmitter<number>();
  isReadonly: boolean;
  private _dialog = inject(MatDialog);

  constructor(private notifyService: StateNotifyService, private dpControlService: VesselDpControlService) {
    this.vessel = this.notifyService.getSelectedVessel();
    this.isReadonly = true;
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngOnInit(): void {
    this.notifyService.vesselSelection$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(model => {
        this.vessel = model;
        this.reloadListDpControl(model);
      });

    this.notifyService.readonlyMode$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(res => {
        this.isReadonly = res;
      });
  }

  reloadListDpControl(ves: Vessel) {
    this.dpControlService.getAllDpControlsOfVessel(ves.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (res: any) => {
          this.dataSource = res;
          this.listSize.emit(this.dataSource.length);
        },
        error: (error: any) => {
          console.error('Error: ', error);
          this.dataSource = [];
        }
      })
  }

  selectVesselDPControl(selected: VesselDpControl) {
    if (!this.isReadonly) {
      var objCopy: VesselDpControl = { ...selected };
      const dialogRef = this._dialog.open(VesselDpControlDetailsComponent, { data: objCopy });
      dialogRef.afterClosed()
        .pipe(takeUntil(this.destroyed$))
        .subscribe(result => {
          if (result !== undefined) {
            if (dialogRef.componentInstance.isToUpdate) {
              this.update(dialogRef.componentInstance.vesselDpControl);
            }
          } else if (dialogRef.componentInstance.isToDelete) {
            this.delete(dialogRef.componentInstance.vesselDpControl.id);
          }
        });
    }
  }

  //--- CRUD actions ---

  create() {
    this.dpControlService.createDpControl(this.vessel.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: model => {
          this.addNewModelToDataSource(model);
          this.selectVesselDPControl(model);
        },
        error: err => { console.log(err); }
      });
  }

  private addNewModelToDataSource(model: VesselDpControl) {
    let list: VesselDpControl[] = [];
    for (var item of this.dataSource) {
      list.push(item);
    }
    list.push(model);
    this.dataSource = list;
    this.listSize.emit(this.dataSource.length);
  }

  update(model: VesselDpControl) {
    this.dpControlService.updateDpControl(model)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: res => {
          this.replaceModelInDataSource(res);
        },
        error: err => { console.log(err); }
      });
  }

  private replaceModelInDataSource(model: VesselDpControl) {
    let list: VesselDpControl[] = [];
    for (var item of this.dataSource) {
      if (item.id === model.id) {
        list.push(model);
      } else {
        list.push(item);
      }
    }
    this.dataSource = list;
    this.listSize.emit(this.dataSource.length);
  }

  delete(modelId: number) {
    this.dpControlService.deleteDpControl(modelId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: res => {
          this.removeModelFromDataSource(modelId);
        },
        error: err => { console.log(err); }
      });
  }

  private removeModelFromDataSource(modelId: number) {
    let list: VesselDpControl[] = [];
    for (var item of this.dataSource) {
      if (item.id === modelId) {
        //the model is to be removed --> do nothing
      } else {
        list.push(item);
      }
    }
    this.dataSource = list;
    this.listSize.emit(this.dataSource.length);
  }


}
